import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { AuctionCard } from "@/components/AuctionCard";
import { api, AUCTION_CATEGORIES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, PackageOpen } from "lucide-react";

const SORTS = [
  { value: "ending", label: "Ending soon" },
  { value: "new", label: "Newly listed" },
  { value: "price", label: "Highest bid" },
];

export default function Marketplace() {
  const [auctions, setAuctions] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("ending");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auctions", { params: { category, search, sort } });
      setAuctions(data.auctions);
    } finally {
      setLoading(false);
    }
  }, [category, search, sort]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Live Auctions</h1>
            <p className="text-slate-400 mt-2">Bid on single items or whole estate lots. 3-day auctions, flat 4% seller fee — no listing fees.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {SORTS.map((s) => (
              <button
                key={s.value}
                data-testid={`sort-${s.value}`}
                onClick={() => setSort(s.value)}
                className={`px-3 h-9 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${
                  sort === s.value ? "bg-amber-600 text-white border-amber-600 font-semibold" : "bg-[#121721] text-slate-300 border-white/10 hover:border-amber-500/40"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              data-testid="marketplace-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search auctions…"
              className="pl-10 h-11 bg-[#121721] border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-amber-500"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto lg:flex-wrap pb-1 -mx-4 px-4 lg:mx-0 lg:px-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <FilterChip active={category === "all"} onClick={() => setCategory("all")} testid="filter-all">All</FilterChip>
            {AUCTION_CATEGORIES.map((c) => (
              <FilterChip key={c.value} active={category === c.value} onClick={() => setCategory(c.value)} testid={`filter-${c.value}`}>
                {c.label}
              </FilterChip>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-24 text-slate-500">Loading auctions…</div>
        ) : auctions.length === 0 ? (
          <div className="text-center py-24 text-slate-500 flex flex-col items-center gap-3">
            <PackageOpen className="h-10 w-10" />
            <p>No live auctions yet. Be the first to list something.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="auctions-grid">
            {auctions.map((a, i) => (
              <AuctionCard key={a.auction_id} auction={a} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const FilterChip = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-4 h-11 rounded-full text-sm font-medium border transition-all shrink-0 whitespace-nowrap ${
      active ? "bg-amber-600 text-white border-amber-600 font-semibold" : "bg-[#121721] text-slate-300 border-white/10 hover:border-amber-500/40"
    }`}
  >
    {children}
  </button>
);
